// Roster side panel of the admin Teams page. Opens instantly from the list row (which already has
// the roster), then GET /api/teams/:id (cached FACEIT) and GET /api/teams/:id/faceit (live FACEIT).
// Every change patches the panel and its list row locally (onTeamChanged) — no full list refetch.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import TeamLogo from './TeamLogo';
import FaceitBadge from './FaceitBadge';
import { IconEdit, IconExternalLink, IconImage, IconPlus, IconTrash, IconUsers } from '../Icons';
import { parseBulkRoster, steam64ToAccountId } from '../../utils/teamImport';

const MAX_IMAGE = 2 * 1024 * 1024;

function avgElo(players) {
  const elos = players.map(p => Number(p.faceit && p.faceit.faceit_elo)).filter(e => Number.isFinite(e) && e > 0);
  return elos.length ? { avg_faceit_elo: Math.round(elos.reduce((a, b) => a + b, 0) / elos.length), faceit_elo_count: elos.length } : { avg_faceit_elo: null, faceit_elo_count: 0 };
}

// teamLogo: the team logo URL when "Use the logo as every player's in-game avatar" is on — the game
// uses it for every player (avatarService), so the row shows it and the own-avatar buttons are hidden
function PlayerRow({ p, busy, teamLogo, onRole, onSave, onRemove, onAvatar, onAvatarRemove }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', steam_id: '' });
  const accountId = steam64ToAccountId(p.steam_id);
  const startEdit = () => { setForm({ name: p.name || '', steam_id: p.steam_id }); setEditing(true); };
  const save = async (e) => {
    e.preventDefault();
    const body = {};
    if (form.name !== (p.name || '')) body.name = form.name; // '' = fetch the Steam name again
    if (form.steam_id.trim() && form.steam_id.trim() !== p.steam_id) body.steam_id = form.steam_id.trim();
    if (!Object.keys(body).length) { setEditing(false); return; }
    if (await onSave(p, body)) setEditing(false);
  };

  return (
    <div className="tm-player">
      <div className="tm-player-av">
        <span className="tm-av" title={teamLogo ? t('teams.avatarIsTeamLogo', 'In-game avatar: the team logo (switch it off under Team logo to use the player\'s own)') : undefined}>
          {teamLogo ? <img src={teamLogo} alt="" />
            : p.avatar_v ? <img src={`/api/avatars/${p.steam_id}?v=${p.avatar_v}`} alt="" /> : String(p.name || '?').trim().charAt(0).toUpperCase()}
        </span>
        {!teamLogo && (
          <label title={t('teams.uploadAvatar', 'Upload avatar (max 2 MB, square works best)')} aria-label={t('teams.uploadAvatar', 'Upload avatar (max 2 MB, square works best)')}>
            +<input type="file" accept="image/*" className="tm-file" disabled={busy} onChange={e => { onAvatar(p, e.target.files[0]); e.target.value = ''; }} />
          </label>
        )}
        {!teamLogo && !!p.avatar_v && <button type="button" title={t('teams.removeAvatar', 'Remove avatar')} aria-label={t('teams.removeAvatar', 'Remove avatar')} onClick={() => onAvatarRemove(p)} disabled={busy}>×</button>}
      </div>

      {editing ? (
        <form className="tm-player-main edit" onSubmit={save}>
          <input className="tm-input" autoFocus value={form.name} placeholder={t('teams.namePlaceholderFetch', 'Name (empty = fetch from Steam)')} onChange={e => setForm({ ...form, name: e.target.value })} />
          <input className="tm-input mono" value={form.steam_id} placeholder={t('teams.steamPlaceholder', 'Steam64, STEAM_0:…, or profile link')} onChange={e => setForm({ ...form, steam_id: e.target.value })} />
          <div className="tm-line end">
            <button type="button" className="tm-btn small" onClick={() => setEditing(false)} disabled={busy}>{t('common.cancel', 'Cancel')}</button>
            <button type="submit" className="tm-btn small primary" disabled={busy}>{busy && <span className="tm-spin" />}{t('common.save', 'Save')}</button>
          </div>
        </form>
      ) : (
        <>
          <div className="tm-player-main">
            <div className="tm-player-name">
              <span>{p.name || t('teams.unknownPlayer', 'Unknown')}</span>
              <FaceitBadge faceit={p.faceit} />
            </div>
            <div className="tm-player-ids">
              <a href={`https://steamcommunity.com/profiles/${p.steam_id}`} target="_blank" rel="noopener noreferrer" title={t('teams.openSteam', 'Open Steam profile')}>{p.steam_id} <IconExternalLink size={9} /></a>
              {accountId && <span title={t('teams.accountIdTitle', 'Account ID, as used in CS2 backup files')}>{t('teams.accountId', 'Account ID: {{id}}', { id: accountId })}</span>}
            </div>
          </div>
          <div className="tm-player-actions">
            <button type="button" className={`tm-role ${p.role === 'sub' ? 'sub' : 'main'}`} disabled={busy} onClick={() => onRole(p)}
              title={p.role === 'sub' ? t('teams.makeMain', 'Make main player') : t('teams.makeSub', 'Make substitute')}>
              {p.role === 'sub' ? t('teams.sub', 'Sub') : t('teams.main', 'Main')}
            </button>
            <button type="button" className="tm-icon-btn" disabled={busy} onClick={startEdit} title={t('common.edit', 'Edit')} aria-label={t('common.edit', 'Edit')}><IconEdit size={14} /></button>
            <button type="button" className="tm-icon-btn" disabled={busy} onClick={() => onRemove(p)} title={t('teams.removePlayer', 'Remove from team')} aria-label={t('teams.removePlayer', 'Remove from team')}><IconTrash size={14} /></button>
          </div>
        </>
      )}
    </div>
  );
}

export default function TeamPanel({ team: rowTeam, onClose, onTeamChanged, onEdit, toast, confirm, escapeBlocked }) {
  const { t } = useTranslation();
  const [team, setTeam] = useState(rowTeam);
  const [busy, setBusy] = useState({}); // { add, bulk, logo, toggle, ['p'+id] }
  const [mode, setMode] = useState('single');
  const [single, setSingle] = useState({ steam: '', name: '', role: 'main' });
  const [bulkText, setBulkText] = useState('');
  const [bulkFails, setBulkFails] = useState([]);
  const [eloLoading, setEloLoading] = useState(true);
  const closeRef = useRef(null);
  const teamId = rowTeam.id;

  // busy state drives the UI; the ref is the actual lock (state is too late for a double click)
  const locks = useRef({});
  const setBusyKey = (k, v) => { locks.current[k] = v; setBusy(b => ({ ...b, [k]: v })); };
  const isLocked = (k) => !!locks.current[k];
  const errText = useCallback((err, fallback) => {
    const d = err && err.response && err.response.data;
    const code = d && d.code;
    const map = {
      already_in_team: t('teams.errAlreadyInTeam', 'This player is already on this team.'),
      bad_steam_id: t('teams.errBadSteam', 'Could not resolve that Steam ID. Use a Steam64, STEAM_0:…, or a profile link.'),
      name_not_fetched: t('teams.errNameNotFetched', "Couldn't fetch the player's name from Steam. Enter the name manually."),
      not_your_player: t('teams.errNotYourPlayer', 'You can only change avatars of players on your own teams.'),
      no_team_image: t('teams.errNoLogo', 'Upload a team logo first.'),
      main_full: t('teams.errMainFull', 'This team already has 5 main players. Add the player as a substitute.'),
      subs_full: t('teams.errSubsFull', 'This team already has 3 substitutes, the most a team can have.'),
      steam_id_taken: t('teams.errSteamTaken', 'This player already plays for "{{team}}". A player can be on one of your teams at a time.', { team: d && d.team }),
    };
    return (code && map[code]) || (d && d.error) || fallback;
  }, [t]);

  // Apply a change to the LATEST team state (two quick actions, or the FACEIT merge landing in the
  // middle of one, must not overwrite each other) and keep the list row in sync.
  const commit = useCallback((update) => {
    setTeam(cur => {
      const next = update(cur);
      onTeamChanged({ ...next, ...avgElo(next.players || []), player_count: (next.players || []).length });
      return next;
    });
  }, [onTeamChanged]);

  // load: cached roster at once, then live FACEIT
  useEffect(() => {
    const ctl = new AbortController();
    setTeam(rowTeam);
    setEloLoading(true);
    setMode('single'); setBulkFails([]); setSingle({ steam: '', name: '', role: 'main' });
    (async () => {
      try {
        const r = await axios.get(`/api/teams/${teamId}`, { signal: ctl.signal });
        setTeam(r.data);
        const f = await axios.get(`/api/teams/${teamId}/faceit`, { signal: ctl.signal, timeout: 60000 });
        const byId = new Map(f.data.players.map(x => [x.id, x.faceit]));
        commit(cur => ({ ...cur, players: (cur.players || []).map(p => (byId.has(p.id) ? { ...p, faceit: byId.get(p.id) } : p)) }));
      } catch (err) {
        if (!axios.isCancel(err) && err.name !== 'CanceledError') toast(errText(err, t('teams.loadPlayersFailed', 'Failed to load players')), 'error');
      } finally {
        if (!ctl.signal.aborted) setEloLoading(false);
      }
    })();
    return () => ctl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  // Escape closes (not while a confirm / the edit form is open on top); focus the close button
  const blockedRef = useRef(escapeBlocked);
  blockedRef.current = escapeBlocked;
  useEffect(() => {
    closeRef.current && closeRef.current.focus();
    const onKey = (e) => { if (e.key === 'Escape' && !blockedRef.current) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const players = useMemo(() => team.players || [], [team.players]);
  const mains = players.filter(p => p.role !== 'sub');
  const subs = players.filter(p => p.role === 'sub');
  const elo = avgElo(players);

  const patchPlayer = (id, patch) => commit(cur => ({ ...cur, players: (cur.players || []).map(p => (p.id === id ? { ...p, ...patch } : p)) }));

  // ── players ──
  const addSingle = async (e) => {
    e.preventDefault();
    if (isLocked('add') || !single.steam.trim()) return;
    setBusyKey('add', true);
    try {
      const r = await axios.post(`/api/teams/${teamId}/players`, { steam_id: single.steam.trim(), name: single.name.trim() || undefined, role: single.role });
      commit(cur => ({ ...cur, players: [...(cur.players || []), { ...r.data, faceit: null }] }));
      setSingle({ steam: '', name: '', role: single.role });
      toast(t('teams.playerAdded', 'Added {{name}}', { name: r.data.name || r.data.steam_id }));
    } catch (err) {
      toast(errText(err, t('teams.addPlayerFailed', 'Failed to add player')), 'error');
    } finally { setBusyKey('add', false); }
  };

  const addBulk = async () => {
    const lines = parseBulkRoster(bulkText).filter(l => l.steam);
    if (!lines.length || isLocked('bulk')) return;
    if (lines.length > 30) { toast(t('teams.bulkTooMany', 'At most 30 players at once.'), 'warning'); return; }
    setBusyKey('bulk', true); setBulkFails([]);
    try {
      const r = await axios.post(`/api/teams/${teamId}/players/bulk`, { players: lines.map(l => ({ steam_id: l.steam, name: l.name || undefined })) }, { timeout: 120000, validateStatus: s => s === 201 || s === 400 });
      if (!r.data.results) throw Object.assign(new Error('bulk'), { response: r });
      const added = r.data.results.filter(x => x.ok).map(x => ({ ...x.player, faceit: null }));
      if (added.length) commit(cur => ({ ...cur, players: [...(cur.players || []), ...added] }));
      const failed = r.data.results.map((x, i) => ({ ...x, line: lines[i] })).filter(x => !x.ok);
      // keep only the failed lines in the box, with their reasons below it
      setBulkText(failed.map(f => f.line.raw).join('\n'));
      setBulkFails(failed.map(f => `${f.line.steam}: ${errText({ response: { data: f } }, f.error)}`));
      toast(failed.length
        ? t('teams.bulkPartial', 'Added {{ok}} of {{total}} players.', { ok: added.length, total: lines.length })
        : t('teams.bulkAdded', 'Added {{count}} players.', { count: added.length }), failed.length ? 'warning' : 'success');
    } catch (err) {
      toast(errText(err, t('teams.addPlayerFailed', 'Failed to add player')), 'error');
    } finally { setBusyKey('bulk', false); }
  };

  const savePlayer = async (p, body) => {
    if (isLocked('p' + p.id)) return false;
    setBusyKey('p' + p.id, true);
    try {
      const r = await axios.put(`/api/teams/${teamId}/players/${p.id}`, body);
      patchPlayer(p.id, { ...r.data.player, faceit: body.steam_id ? null : p.faceit });
      toast(t('teams.playerUpdated', 'Player updated'));
      return true;
    } catch (err) {
      toast(errText(err, t('teams.updatePlayerFailed', 'Failed to update player')), 'error');
      return false;
    } finally { setBusyKey('p' + p.id, false); }
  };

  const toggleRole = async (p) => {
    const role = p.role === 'sub' ? 'main' : 'sub';
    if (isLocked('p' + p.id)) return false;
    setBusyKey('p' + p.id, true);
    try {
      await axios.put(`/api/teams/${teamId}/players/${p.id}`, { role });
      patchPlayer(p.id, { role });
    } catch (err) {
      toast(errText(err, t('teams.updatePlayerFailed', 'Failed to update player')), 'error');
    } finally { setBusyKey('p' + p.id, false); }
  };

  const removePlayer = (p) => confirm(t('teams.removePlayerConfirm', 'Remove {{name}} from {{team}}?', { name: p.name || p.steam_id, team: team.name }), async () => {
    if (isLocked('p' + p.id)) return false;
    setBusyKey('p' + p.id, true);
    try {
      await axios.delete(`/api/teams/${teamId}/players/${p.id}`);
      commit(cur => ({ ...cur, players: (cur.players || []).filter(x => x.id !== p.id) }));
      toast(t('teams.playerRemoved', 'Removed {{name}}', { name: p.name || p.steam_id }));
    } catch (err) {
      toast(errText(err, t('teams.removePlayerFailed', 'Failed to remove player')), 'error');
    } finally { setBusyKey('p' + p.id, false); }
  }, t('teams.remove', 'Remove'));

  const tooBig = (file) => {
    if (file && file.size > MAX_IMAGE) { toast(t('teams.imageTooLarge', 'Image too large ({{kb}} KB). Max 2 MB.', { kb: Math.round(file.size / 1024) }), 'error'); return true; }
    return false;
  };

  const uploadAvatar = async (p, file) => {
    if (!file || tooBig(file)) return;
    if (isLocked('p' + p.id)) return false;
    setBusyKey('p' + p.id, true);
    try {
      const fd = new FormData(); fd.append('avatar', file);
      const r = await axios.post(`/api/avatars/${p.steam_id}`, fd);
      patchPlayer(p.id, { avatar_v: r.data.avatar_v || Date.now() });
      toast(t('teams.avatarUploaded', 'Avatar updated'));
    } catch (err) {
      toast(errText(err, t('teams.avatarUploadFailed', 'Avatar upload failed')), 'error');
    } finally { setBusyKey('p' + p.id, false); }
  };

  const removeAvatar = (p) => confirm(t('teams.removeAvatarConfirm', 'Remove the in-game avatar of {{name}}?', { name: p.name || p.steam_id }), async () => {
    if (isLocked('p' + p.id)) return false;
    setBusyKey('p' + p.id, true);
    try {
      await axios.delete(`/api/avatars/${p.steam_id}`);
      patchPlayer(p.id, { avatar_v: 0 });
      toast(t('teams.avatarRemoved', 'Avatar removed'));
    } catch (err) {
      toast(errText(err, t('teams.avatarRemoveFailed', 'Failed to remove avatar')), 'error');
    } finally { setBusyKey('p' + p.id, false); }
  }, t('teams.remove', 'Remove'));

  // ── team logo + "use as every player's in-game avatar" ──
  const uploadLogo = async (file) => {
    if (!file || tooBig(file) || isLocked('logo')) return;
    setBusyKey('logo', true);
    try {
      const fd = new FormData(); fd.append('avatar', file);
      const r = await axios.post(`/api/teams/${teamId}/avatar`, fd);
      commit(cur => ({ ...cur, avatar_v: r.data.avatar_v || Date.now(), has_team_image: 1, team_avatar: 1 }));
      toast(t('teams.logoUploaded', 'Logo uploaded - players use it as their in-game avatar (switch it off below)'));
    } catch (err) {
      toast(errText(err, t('teams.logoUploadFailed', 'Logo upload failed')), 'error');
    } finally { setBusyKey('logo', false); }
  };

  const removeLogo = () => confirm(t('teams.removeLogoConfirm', 'Remove the logo of {{team}}?', { team: team.name }), async () => {
    setBusyKey('logo', true);
    try {
      await axios.delete(`/api/teams/${teamId}/avatar`);
      commit(cur => ({ ...cur, avatar_v: 0, has_team_image: 0, team_avatar: 0 }));
      toast(t('teams.logoRemoved', 'Logo removed'));
    } catch (err) {
      toast(errText(err, t('teams.logoRemoveFailed', 'Failed to remove the logo')), 'error');
    } finally { setBusyKey('logo', false); }
  }, t('teams.remove', 'Remove'));

  const hasLogo = !!team.avatar_v;
  const toggleTeamAvatar = async () => {
    if (!hasLogo || isLocked('toggle')) return;
    const enabled = !team.team_avatar;
    setBusyKey('toggle', true);
    try {
      await axios.put(`/api/teams/${teamId}/team-avatar`, { enabled });
      commit(cur => ({ ...cur, team_avatar: enabled ? 1 : 0 }));
    } catch (err) {
      toast(errText(err, t('teams.toggleFailed', 'Could not change the setting')), 'error');
    } finally { setBusyKey('toggle', false); }
  };

  // the uploaded logo is every player's in-game avatar while the switch is on (same file as TeamLogo)
  const teamLogo = hasLogo && team.team_avatar ? `/api/teams/${teamId}/avatar?v=${team.avatar_v}` : null;
  const rowProps = { teamLogo, onRole: toggleRole, onSave: savePlayer, onRemove: removePlayer, onAvatar: uploadAvatar, onAvatarRemove: removeAvatar };
  const bulkCount = parseBulkRoster(bulkText).filter(l => l.steam).length;

  return (
    <>
      <motion.div className="tm-panel-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
      <motion.aside className="tm-panel" role="dialog" aria-modal="true" aria-label={t('teams.rosterOf', 'Roster of {{name}}', { name: team.name })}
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'tween', duration: 0.22 }}>
        <div className="tm-panel-head">
          <TeamLogo team={team} size={44} radius={10} />
          <div className="tm-panel-who">
            <div className="tm-panel-name">
              <span className="tm-team-name">{team.name}</span>
              {team.tag && <span className="tm-tag">{team.tag}</span>}
            </div>
            <div className="tm-panel-meta">
              <span>{subs.length
                ? t('teams.mainSubCount', '{{main}} + {{subs}} sub', { main: mains.length, subs: subs.length })
                : t('teams.playerCount', '{{count}} players', { count: players.length })}</span>
              {elo.avg_faceit_elo
                ? <span className="tm-elo" title={t('teams.avgEloTitle', 'Average FACEIT ELO of {{count}} linked players', { count: elo.faceit_elo_count })}>{t('teams.eloShort', 'ELO {{elo}}', { elo: elo.avg_faceit_elo })}</span>
                : eloLoading ? <span>{t('teams.eloLoading', 'Loading ELO…')}</span> : null}
              {team.coach_name && <span>{t('teams.coachLabel', 'Coach: {{name}}', { name: team.coach_name })}</span>}
            </div>
          </div>
          <Link className="tm-btn small" to={`/admin/teams/${team.id}/seating`} title={t('seating.teamButtonHint', 'Default stage seating for this team’s matches')}><IconUsers size={12} /> {t('seating.button', 'Seating')}</Link>
          <button type="button" className="tm-btn small" onClick={() => onEdit(team)}><IconEdit size={12} /> {t('common.edit', 'Edit')}</button>
          <button type="button" ref={closeRef} className="tm-icon-btn" onClick={onClose} aria-label={t('common.close', 'Close')}>×</button>
        </div>

        <div className="tm-panel-body">
          {/* players */}
          <section className="tm-section">
            <h3 className="tm-section-title">{t('teams.mainPlayers', 'Main players')} <span className="tm-section-count">{mains.length}</span></h3>
            {mains.length ? <div className="tm-players">{mains.map(p => <PlayerRow key={p.id} p={p} busy={!!busy['p' + p.id]} {...rowProps} />)}</div>
              : <p className="tm-hint flush">{t('teams.noMainPlayers', 'No main players yet - add them below.')}</p>}
            {subs.length > 0 && (
              <>
                <h3 className="tm-section-title sub">{t('teams.subs', 'Substitutes')} <span className="tm-section-count">{subs.length}</span></h3>
                <div className="tm-players">{subs.map(p => <PlayerRow key={p.id} p={p} busy={!!busy['p' + p.id]} {...rowProps} />)}</div>
              </>
            )}
          </section>

          {/* add players */}
          <section className="tm-section">
            <div className="tm-section-title">
              {t('teams.addPlayers', 'Add players')}
              <span className="tm-spacer" />
              <span className="tm-seg">
                <button type="button" className={mode === 'single' ? 'on' : ''} onClick={() => setMode('single')}>{t('teams.single', 'Single')}</button>
                <button type="button" className={mode === 'bulk' ? 'on' : ''} onClick={() => setMode('bulk')}>{t('teams.bulk', 'Bulk')}</button>
              </span>
            </div>
            {mode === 'single' ? (
              <form onSubmit={addSingle} className="tm-stack">
                <div className="tm-row-inputs">
                  <input className="tm-input mono" value={single.steam} onChange={e => setSingle({ ...single, steam: e.target.value })} placeholder={t('teams.steamPlaceholder', 'Steam64, STEAM_0:…, or profile link')} disabled={busy.add} />
                  <input className="tm-input" value={single.name} onChange={e => setSingle({ ...single, name: e.target.value })} placeholder={t('teams.nameOptional', 'Name (optional - fetched from Steam)')} disabled={busy.add} />
                </div>
                <div className="tm-line">
                  <span className="tm-seg">
                    <button type="button" className={single.role === 'main' ? 'on' : ''} onClick={() => setSingle({ ...single, role: 'main' })}>{t('teams.main', 'Main')}</button>
                    <button type="button" className={single.role === 'sub' ? 'on' : ''} onClick={() => setSingle({ ...single, role: 'sub' })}>{t('teams.sub', 'Sub')}</button>
                  </span>
                  <span className="tm-spacer" />
                  <button type="submit" className="tm-btn primary" disabled={busy.add || !single.steam.trim()}>{busy.add ? <span className="tm-spin" /> : <IconPlus size={13} />} {t('teams.addToRoster', 'Add to roster')}</button>
                </div>
              </form>
            ) : (
              <div className="tm-stack">
                <textarea className="tm-input mono" rows={5} value={bulkText} onChange={e => setBulkText(e.target.value)} disabled={busy.bulk} spellCheck={false}
                  placeholder={'76561198000000001 - Player One\nhttps://steamcommunity.com/id/player2\nSTEAM_0:1:12345, Player Three'} />
                <p className="tm-hint flush">{t('teams.bulkRosterHint', 'One player per line: Steam ID, optionally followed by " - name", ", name" or "| name". Added as main players; switch them to Sub afterwards.')}</p>
                {bulkFails.length > 0 && <div className="tm-fail-list">{bulkFails.map((f, i) => <span key={i}>{f}</span>)}</div>}
                <div className="tm-line end">
                  <button type="button" className="tm-btn primary" disabled={busy.bulk || !bulkCount} onClick={addBulk}>{busy.bulk && <span className="tm-spin" />}{t('teams.addCount', 'Add {{count}} players', { count: bulkCount })}</button>
                </div>
              </div>
            )}
          </section>

          {/* logo */}
          <section className="tm-section">
            <h3 className="tm-section-title">{t('teams.logo', 'Team logo')}</h3>
            <div className="tm-logo-line">
              <TeamLogo team={team} size={56} radius={10} />
              <label className={`tm-btn${busy.logo ? ' busy' : ''}`}>
                {busy.logo ? <span className="tm-spin" /> : <IconImage size={13} />} {hasLogo ? t('teams.replaceLogo', 'Replace') : t('teams.uploadLogo', 'Upload logo')}
                <input type="file" accept="image/*" className="tm-file" disabled={busy.logo} onChange={e => { uploadLogo(e.target.files[0]); e.target.value = ''; }} />
              </label>
              {hasLogo && <button type="button" className="tm-btn danger" disabled={busy.logo} onClick={removeLogo}><IconTrash size={13} /> {t('teams.remove', 'Remove')}</button>}
            </div>
            <p className="tm-hint">{t('teams.logoHint', 'Max 2 MB, a square image works best.')}</p>
            <div className="tm-switch-wrap">
              <span className={`tm-switch${team.team_avatar && hasLogo ? ' on' : ''}`} role="switch" tabIndex={hasLogo ? 0 : -1}
                aria-checked={!!(team.team_avatar && hasLogo)} aria-disabled={!hasLogo || !!busy.toggle}
                onClick={toggleTeamAvatar} onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleTeamAvatar(); } }}>
                <span className="track" />
                {t('teams.useLogoInGame', 'Use the logo as every player\'s in-game avatar')}
              </span>
              {!hasLogo && <p className="tm-hint">{t('teams.useLogoNeedsLogo', 'Upload a logo first.')}</p>}
            </div>
          </section>
        </div>
      </motion.aside>
    </>
  );
}
